import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { ErrorResponse, Tenant, TenantColorConfig } from '@clientwire/api';
import { WireApiService } from '@frontend/shared';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

export type Language = {
  code: string;
  name: string;
};

export type DomainType = 'client' | 'main';

@Injectable({ providedIn: 'root' })
export class GeneralService {
  readonly #wireApiService = inject(WireApiService);
  readonly #translateService = inject(TranslateService);
  readonly #toastr = inject(ToastrService);

  readonly availableLanguages: WritableSignal<Language[]> = signal([
    { code: 'en', name: 'English' },
    { code: 'de', name: 'Deutch' },
  ]);
  readonly tenant: WritableSignal<Tenant | null> = signal(null);

  readonly tenantLoading = signal(false);
  readonly languageLoading = signal(false);

  updateTenant(requestBody: { [key: string]: any }): void {
    if (!this.tenant) return;

    this.languageLoading.set(true);

    this.#wireApiService.tenantsApi
      .updateTenant({ requestBody })
      .then((response) => {
        this.tenant.set(response);

        this.#toastr.success(
          this.#translateService.instant('SETTINGS.GENERAL.TENANT_UPDATED_MESSAGE'),
          this.#translateService.instant('SETTINGS.GENERAL.TENANT_UPDATED_TITLE')
        );
      })
      .catch(() => {
        this.handleError('ERROR_UPDATING');
      });
    this.languageLoading.set(false);
  }

  async updateTenantColorConfig(requestBody: TenantColorConfig): Promise<void> {
    await this.#wireApiService.tenantColorConfigApi.createOrUpdateTenantColorConfig({
      tenantColorConfig: requestBody,
    });
  }

  loadTenantInfo(): void {
    this.tenantLoading.set(true);
    this.#wireApiService.tenantsApi
      .getTenant()
      .then((response) => {
        this.tenant.set(response);
      })
      .catch(this.handleError('ERROR_LOADING'))
      .finally(() => {
        this.tenantLoading.set(false);
      });
  }

  private handleError(errorType: string) {
    return async (error: any) => {
      console.error(`Error in tenant operation (${errorType}):`, error);

      let errorMessage = this.#translateService.instant(`SETTINGS.GENERAL.${errorType}_MESSAGE`);
      try {
        if (error.response) {
          const errorData: ErrorResponse = await error.response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        }
      } catch (e) {
        console.error('Error parsing error response:', e);
      }

      this.#toastr.error(
        errorMessage,
        this.#translateService.instant(`SETTINGS.GENERAL.${errorType}_TITLE`)
      );
    };
  }
}
